import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import { Plus, Ban } from "lucide-react";

import { DataTableNew } from "@/components/shared/DataTable";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import SearchInput from "@/components/shared/SearchInput";
import { Filter, FilterPill, FilterSelect } from "@/components/shared/Filter";
import Skeleton from "@/components/shared/Skeleton";
import StatusBadge from "@/components/shared/StatusBadge";
import AlertModal from "@/components/shared/AlertModal";
import CustomTooltip from "@/components/shared/CustomTooltip";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import { useAuth } from "@/hooks/useAuth";
import useLiveResource from "@/hooks/useLiveResource";
import { PlatformService } from "@/services/platformService";
import { FeedbackService } from "@/services/feedbackService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { messageForError } from "@/lib/errorMessages";
import { formatDate } from "@/utils/dateUtils";

import InviteSuperAdminModal from "./components/InviteSuperAdminModal";

// Same source AddEditModel.jsx previews a tenant's own subdomain against —
// the root domain the platform is served from.
const DOMAIN_BASE = import.meta.env.VITE_DOMAIN_BASE;

// Revoked invitations surface as "Inactive" in the Status column (see
// BADGE_BY_STATUS → StatusBadge renders isActive:false as Inactive), so the
// filter carries the same word — it used to say "Revoked", and there was no
// way to ask the list for inactive SuperAdmins by the name it shows them.
const STATUS_OPTIONS = [
    { label: "Active", value: "accepted" },
    { label: "Pending", value: "pending" },
    { label: "Inactive", value: "revoked" },
];

// What revoking one SuperAdmin does to the feedback email lists, which only
// SuperAdmins are on. The server applies the same rule on revoke.
//   kind     the list they are on ("To", "CC", "BCC"), or null
//   blocked  the only To — refused, because feedback mail goes out only with
//            a To
const feedbackImpact = (lists, id) => {
    const on = (name) => (lists?.[name] ?? []).some((p) => p.id === id);
    const kind = on("to") ? "To" : on("cc") ? "CC" : on("bcc") ? "BCC" : null;
    return { kind, blocked: kind === "To" && (lists?.to ?? []).length === 1 };
};

const BADGE_BY_STATUS = {
    accepted: { isActive: true },
    pending: { isActive: true, text: "Pending", colorIndex: 4 },
    revoked: { isActive: false },
};

// SuperAdmins page — platform-level admin list.
// Rows come from GET /api/v2/platform/superadmins/invitations (camelCase).
const SuperAdmins = () => {
    const setHeader = useOutletContext();
    const { user: currentUser } = useAuth();
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [silentFetch, setSilentFetch] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [revokeInFlight, setRevokeInFlight] = useState(false);
    // The feedback lists, read when the revoke dialog opens: "loading", a
    // `feedbackImpact` result, or null when they could not be read (the
    // server still applies the rule).
    const [revokeImpact, setRevokeImpact] = useState(null);
    const [tableData, setTableData] = useState({ total: 0, isLoading: false });

    // Update the outlet header when the fetched count changes.
    useEffect(() => {
        setHeader({
            title: "SuperAdmins",
            subTitle: tableData.isLoading ? (
                <Skeleton className="h-5 w-28" />
            ) : (
                `Total ${tableData.total} SuperAdmin${tableData.total === 1 ? "" : "s"}`
            ),
        });
        return () => setHeader({ title: "", subTitle: "" });
    }, [tableData, setHeader]);

    useEffect(() => {
        if (!silentFetch) return;
        const timer = setTimeout(() => setSilentFetch(false), 200);
        return () => clearTimeout(timer);
    }, [silentFetch]);

    // An invitee accepting (or another SuperAdmin revoking) happens in a
    // different session — this list otherwise only learns about it on the
    // next manual refresh. `superadmin.changed` fires on invite, accept and
    // revoke; a plain silent refetch is enough since this screen always
    // reads the current state rather than an event payload.
    useLiveResource({
        keys: "superadmin",
        onChange: () => setSilentFetch(true),
    });

    const onDataFetch = (data) => setTableData(data);

    const COLUMNS = useMemo(
        () => [
            {
                key: "invitedName",
                label: "Name",
                sortable: true,
                align: "left",
                render: (val) => (
                    <div className="min-w-0 max-w-[240px]">
                        <span
                            className="block overflow-hidden text-ellipsis whitespace-nowrap text-left font-medium text-[13px]"
                            title={val}
                        >
                            {val || "—"}
                        </span>
                    </div>
                ),
            },
            {
                key: "invitedEmail",
                label: "Email",
                sortable: true,
                align: "left",
                render: (val, row) => (
                    <div className="min-w-0 max-w-[280px]">
                        <span
                            className="block overflow-hidden text-ellipsis whitespace-nowrap text-left font-medium text-[13px]"
                            title={val || row?.adUsername}
                        >
                            {val || row?.adUsername || "—"}
                        </span>
                    </div>
                ),
            },
            {
                key: "authProvider",
                label: "Provider",
                sortable: false,
                align: "center",
                render: (val) => (
                    <span className="text-text-primary text-[12px] uppercase tracking-wide">
                        {val === "ad" ? "AD" : "Email"}
                    </span>
                ),
            },
            {
                key: "status",
                label: "Status",
                sortable: true,
                align: "center",
                render: (val) => (
                    <div className="flex justify-center">
                        <StatusBadge {...(BADGE_BY_STATUS[val] || { isActive: false })} />
                    </div>
                ),
            },
            {
                key: "createdAt",
                label: "Invited On",
                sortable: true,
                align: "center",
                render: (val) => (
                    <span className="text-text-primary text-[12px]">
                        {val ? formatDate(val) : "—"}
                    </span>
                ),
            },
            {
                key: "id",
                label: "Action",
                sortable: false,
                align: "center",
                render: (val, row) => {
                    const isRevoked = row?.status === "revoked";
                    const isSelf =
                        !!currentUser?.id &&
                        String(currentUser.id) === String(row?.id);
                    return (
                        <div className="flex gap-2 justify-center">
                            <PermissionGuard
                                permissions={[PERMISSIONS.SUPER_ADMIN_MANAGE]}
                                isComponent
                            >
                                <CustomTooltip
                                    text={
                                        isSelf
                                            ? "You cannot revoke your own access"
                                            : isRevoked
                                              ? "Already revoked"
                                              : "Revoke SuperAdmin access"
                                    }
                                >
                                    <Button
                                        onClick={() =>
                                            !isRevoked &&
                                            !isSelf &&
                                            setRevokeTarget(row)
                                        }
                                        disabled={isRevoked || isSelf}
                                        className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background-icon"
                                    >
                                        <Ban size={16} />
                                    </Button>
                                </CustomTooltip>
                            </PermissionGuard>
                        </div>
                    );
                },
            },
        ],
        [currentUser]
    );

    const filters = useMemo(
        () => [
            {
                key: "status",
                label: "All Statuses",
                value: statusFilter,
                onChange: setStatusFilter,
                options: STATUS_OPTIONS,
            },
        ],
        [statusFilter]
    );

    const activeFilters = useMemo(() => {
        const selected = STATUS_OPTIONS.find((s) => s.value === statusFilter);
        return [
            statusFilter && {
                key: "status",
                label: `Status: ${selected?.label}`,
                clear: () => setStatusFilter(""),
            },
        ].filter(Boolean);
    }, [statusFilter]);

    const handleClearAll = () => setStatusFilter("");

    const extraParams = useMemo(
        () => ({ status: statusFilter || undefined }),
        [statusFilter]
    );

    useEffect(() => {
        if (!revokeTarget) return undefined;
        let live = true;
        setRevokeImpact("loading");
        FeedbackService.recipients()
            .then((res) => live && setRevokeImpact(feedbackImpact(res?.data, revokeTarget.id)))
            .catch(() => live && setRevokeImpact(null));
        return () => {
            live = false;
        };
    }, [revokeTarget]);

    const revokeName = revokeTarget ? revokeTarget.invitedName || revokeTarget.invitedEmail : "";
    const revokeDescription = !revokeTarget
        ? ""
        : revokeImpact?.blocked
          ? `${revokeName} is the only To recipient of feedback emails. Add another SuperAdmin as a To recipient in Feedback → Email settings first, then revoke.`
          : [
                `${revokeName} will no longer be able to sign in on platform.${DOMAIN_BASE}. They keep any tenant access they have. This action is reversible — invite or promote them again to restore access.`,
                revokeImpact?.kind && `This also removes them from the feedback email settings (${revokeImpact.kind}).`,
            ]
                .filter(Boolean)
                .join(" ");

    const onRevokeConfirm = useCallback(async () => {
        if (!revokeTarget) return;
        try {
            setRevokeInFlight(true);
            await PlatformService.revokeSuperAdminInvitation(
                revokeTarget.id,
                "Revoked by SuperAdmin"
            );
            toast.success("SuperAdmin access revoked");
            setRevokeTarget(null);
            setSilentFetch(true);
        } catch (err) {
            toast.error(messageForError(err, apiErrorMessageFormat(err)));
        } finally {
            setRevokeInFlight(false);
        }
    }, [revokeTarget]);

    return (
        <PermissionGuard permission={PERMISSIONS.SUPER_ADMIN_MANAGE}>
            <div className="flex flex-col gap-2.5" style={{ zoom: 0.85 }}>
                <div className="flex items-center gap-2.5">
                    <div className="flex-1">
                        <SearchInput
                            value={search}
                            onChange={setSearch}
                            placeholder="Search by name or email"
                        />
                    </div>

                    <Filter
                        filters={filters}
                        activeFilters={activeFilters}
                        showFilters={showFilters}
                        onToggle={() => setShowFilters((p) => !p)}
                        onClearAll={handleClearAll}
                        renderFilter={(f) => <FilterSelect key={f.key} {...f} />}
                        renderActiveFilter={(f) => <FilterPill key={f.key} {...f} />}
                    />

                    <PermissionGuard
                        permission={PERMISSIONS.SUPER_ADMIN_MANAGE}
                        isComponent
                    >
                        <Button
                            onClick={() => setInviteOpen(true)}
                            className="hidden md:flex bg-button-secondary text-text-base rounded-full px-3 py-2 items-center gap-1 text-[11px] whitespace-nowrap shrink-0"
                        >
                            <Plus size={11} />
                            Invite SuperAdmin
                        </Button>
                    </PermissionGuard>
                </div>

                {/* Mobile FAB */}
                <div className="fixed bottom-0 left-0 right-0 z-[110] md:hidden px-4 py-3">
                    <PermissionGuard
                        permission={PERMISSIONS.SUPER_ADMIN_MANAGE}
                        isComponent
                    >
                        <Button
                            onClick={() => setInviteOpen(true)}
                            className="w-full bg-button-secondary text-text-base rounded-full py-2.5 flex items-center justify-center gap-1.5 text-[13px]"
                        >
                            <Plus size={14} />
                            Invite SuperAdmin
                        </Button>
                    </PermissionGuard>
                </div>

                <Card className="p-0 bg-table-heading border-border-primary">
                    <DataTableNew
                        key="superadmins-list"
                        className="rounded-none"
                        columns={COLUMNS}
                        api={PlatformService.fetchSuperAdminInvitations}
                        search={search}
                        dataPath="items"
                        extraParams={extraParams}
                        onDataFetch={onDataFetch}
                        silentFetch={silentFetch}
                        paginationLabel="SuperAdmins"
                    />
                </Card>
            </div>

            {inviteOpen && (
                <InviteSuperAdminModal
                    onClose={() => setInviteOpen(false)}
                    onInvited={() => {
                        setInviteOpen(false);
                        setSilentFetch(true);
                    }}
                />
            )}

            <AlertModal
                open={!!revokeTarget}
                onOpenChange={(o) => !o && !revokeInFlight && setRevokeTarget(null)}
                title="Revoke SuperAdmin access?"
                description={revokeDescription}
                cancelText="Cancel"
                actionText={revokeInFlight ? "Revoking…" : "Revoke"}
                actionDisabled={revokeImpact === "loading" || !!revokeImpact?.blocked}
                onCancel={() => setRevokeTarget(null)}
                onAction={onRevokeConfirm}
                actionClassName="bg-red-600 hover:bg-red-700 text-text-base"
            />
        </PermissionGuard>
    );
};

export default SuperAdmins;
